import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAxios, useGenericMutation, useGenericQuery, useParamsApi } from "../hooks";
import {
    Claims,
    DefaultPagedResponse,
    Entity,
    IUseQueryPaged,
    PagedResponse,
    QueryHookParams,
    QueryType,
    Tenant,
} from "../models";
import { Operation } from "rfc6902";
import { useAuth, usePermissions } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";

export const useTenantQuery = (params?: QueryHookParams): IUseQueryPaged<Tenant> => {
    const tenantId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { get, post, patch, del } = useAxios();
    const { isAuthorized } = useAuth();
    const { hasAnyPermission } = usePermissions();
    const { queryParams, sortParams, pageParams } = useParamsApi(Entity.Tenants);
    const url = Endpoints.Tenants;
    const queryType = getQueryType(tenantId, params?.queryType);

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Tenant>>(getRequestOptions(url, config, queryParams, sortParams, pageParams)),
        queryKey: queryKeys.tenants.pagedList({ ...queryParams, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: hasAnyPermission([Claims.ViewAnyTenant, Claims.ViewChildTenant]) && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Tenant>>(
                getRequestOptions(url, config, queryParams, sortParams, undefined, false, true),
            ).then((response) => response.items),
        queryKey: queryKeys.tenants.list({ ...queryParams, ...sortParams }),
        placeholderData: [],
        enabled: hasAnyPermission([Claims.ViewAnyTenant, Claims.ViewChildTenant]) && queryType === QueryType.List,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Tenant>({ url: `${url}/${tenantId}`, config }),
        queryKey: queryKeys.tenants.detail(tenantId),
        enabled: isAuthorized && queryType === QueryType.Detail,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<Tenant>) => post<Partial<Tenant>, Tenant>({ url, data }),
        queryKey: queryKeys.tenants.lists(),
        keysToInvalidate: [queryKeys.tenants.lists(), queryKeys.tenants.pagedLists()],
        successToast: "Tenant created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], Tenant>({ url: `${url}/${tenantId}`, data }),
        queryKey: queryKeys.tenants.detail(tenantId),
        keysToInvalidate: [queryKeys.tenants.lists(), queryKeys.tenants.pagedLists()],
        successToast: "Tenant updated",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${tenantId}` }),
        queryKey: queryKeys.tenants.detail(tenantId),
        keysToInvalidate: [queryKeys.tenants.lists(), queryKeys.tenants.pagedLists()],
        successToast: "Tenant deactivated",
    });

    return useMemo<IUseQueryPaged<Tenant>>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            createMutation,
            updateMutation,
            deleteMutation,
        }),
        [pagedListQuery, listQuery, detailQuery, createMutation, updateMutation, deleteMutation],
    );
};
